import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiDollarSign,
  FiCheckCircle,
  FiAlertTriangle,
  FiClock,
  FiList,
  FiRefreshCw,
  FiShield,
  FiArchive,
  FiCreditCard,
  FiSearch,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import reconciliationService from "../../services/reconciliationService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export default function CashReconciliationAudit() {
  const { user } = useApp();
  const [summary, setSummary] = useState(null);
  const [handovers, setHandovers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("handovers"); // handovers | deposits

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, handRes, depRes] = await Promise.allSettled([
        reconciliationService.getDashboardSummaryStats(),
        reconciliationService.getPendingHandovers(),
        reconciliationService.getDeposits(),
      ]);

      if (sumRes.status === "fulfilled") setSummary(sumRes.value.data);
      if (handRes.status === "fulfilled") setHandovers(handRes.value.data || []);
      if (depRes.status === "fulfilled") setDeposits(depRes.value.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load reconciliation audit data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        Loading College Cash Reconciliation Audit…
      </div>
    );
  }

  const hSum = summary?.handoverSummary || {};
  const dSum = summary?.depositSummary || {};

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header" style={{ marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Cash Collection &amp; Bank Deposit Audit 🔍</h1>
          <p className="page-subtitle">
            Complete traceability of student cash collections, clerk handovers, and accountant bank deposits for this college.
          </p>
        </div>
        <div>
          <button className="btn btn-outline" onClick={loadData}>
            <FiRefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Audit KPI Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "0.85rem",
          marginBottom: "1.5rem",
        }}
      >
        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #003b67" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Total Cash Handed Over
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#003b67", marginTop: 4 }}>
            {inr(hSum.totalActualHandedOver)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Expected: {inr(hSum.totalExpectedCash)}
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #16a34a" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Deposited into College Bank
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#16a34a", marginTop: 4 }}>
            {inr(dSum.totalBankDeposited)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Across {dSum.totalDeposits || 0} deposit challan(s)
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Cash in Accountant Custody
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#f59e0b", marginTop: 4 }}>
            {inr(summary?.cashInAccountantVault)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {summary?.pendingDepositsCount || 0} batch(es) pending deposit
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #ef4444" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Clerk Shortages
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#ef4444", marginTop: 4 }}>
            {inr(hSum.totalClerkShortage)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Excess: {inr(hSum.totalClerkExcess)}
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #8b5cf6" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Deposit Delays
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#8b5cf6", marginTop: 4 }}>
            {dSum.totalDelayedDeposits || 0}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Legitimate banking reasons verified
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: "0.5rem", borderBottom: "2px solid var(--border, #e2e8f0)", marginBottom: "1.25rem" }}>
        <button
          onClick={() => setActiveTab("handovers")}
          style={{
            padding: "0.6rem 1.1rem",
            border: "none",
            background: "none",
            borderBottom: activeTab === "handovers" ? "3px solid #003b67" : "3px solid transparent",
            color: activeTab === "handovers" ? "#003b67" : "var(--text-muted)",
            fontWeight: activeTab === "handovers" ? 700 : 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
          }}
        >
          <FiList size={14} /> Clerk Handovers ({handovers.length})
        </button>
        <button
          onClick={() => setActiveTab("deposits")}
          style={{
            padding: "0.6rem 1.1rem",
            border: "none",
            background: "none",
            borderBottom: activeTab === "deposits" ? "3px solid #003b67" : "3px solid transparent",
            color: activeTab === "deposits" ? "#003b67" : "var(--text-muted)",
            fontWeight: activeTab === "deposits" ? 700 : 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
          }}
        >
          <FiCreditCard size={14} /> Bank Deposits ({deposits.length})
        </button>
      </div>

      {/* ── TAB: HANDOVERS ── */}
      {activeTab === "handovers" && (
        <div className="card" style={{ padding: "1.25rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.75rem" }}>
            Clerk Handover Audit Trail
          </h3>
          {handovers.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
              No handovers recorded yet.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Handover No</th>
                    <th>Clerk</th>
                    <th>Date</th>
                    <th>Expected</th>
                    <th>Actual Handed Over</th>
                    <th>Difference</th>
                    <th>Status</th>
                    <th>Accountant</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {handovers.map((h) => (
                    <tr key={h._id}>
                      <td style={{ fontWeight: 600 }}>{h.handoverNo}</td>
                      <td style={{ fontWeight: 600 }}>{h.clerk?.displayName || h.clerk?.username}</td>
                      <td>{h.collectionDate}</td>
                      <td>{inr(h.expectedAmount)}</td>
                      <td style={{ fontWeight: 700 }}>{inr(h.actualAmount)}</td>
                      <td>
                        {h.difference === 0 ? (
                          <span className="badge badge-success">Balanced</span>
                        ) : h.difference < 0 ? (
                          <span className="badge badge-danger">Shortage: -₹{Math.abs(h.difference)}</span>
                        ) : (
                          <span className="badge badge-warning">Excess: +₹{h.difference}</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            h.status === "Bank_Deposited"
                              ? "badge-success"
                              : h.status === "Received" || h.status === "Discrepancy_Noted"
                              ? "badge-info"
                              : "badge-warning"
                          }`}
                        >
                          {h.status}
                        </span>
                      </td>
                      <td>{h.accountant?.displayName || h.accountant?.username || "—"}</td>
                      <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {h.clerkRemarks || h.accountantRemarks || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: BANK DEPOSITS ── */}
      {activeTab === "deposits" && (
        <div className="card" style={{ padding: "1.25rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.75rem" }}>
            College Bank Deposits
          </h3>
          {deposits.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
              No bank deposits recorded.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Deposit No</th>
                    <th>Bank &amp; Account</th>
                    <th>Deposit Date</th>
                    <th>Challan / Slip No</th>
                    <th>Amount Deposited</th>
                    <th>Delay Status</th>
                    <th>Reconciliation Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((d) => (
                    <tr key={d._id}>
                      <td style={{ fontWeight: 600 }}>{d.depositNo}</td>
                      <td>
                        <div>
                          <strong>{d.bankName}</strong>
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>A/C: {d.bankAccountNumber}</div>
                      </td>
                      <td>{new Date(d.depositDate).toLocaleDateString("en-IN")}</td>
                      <td>
                        <code>{d.challanSlipNo}</code>
                      </td>
                      <td style={{ fontWeight: 700 }}>{inr(d.amountDeposited)}</td>
                      <td>
                        {d.isDelayed ? (
                          <div>
                            <span className="badge badge-warning">Delayed ({d.delayDays}d)</span>
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                              {d.delayReason || "Bank holiday / closure"}
                            </div>
                          </div>
                        ) : (
                          <span className="badge badge-success">Prompt Deposit</span>
                        )}
                      </td>
                      <td>
                        {d.status === "Deposited" || d.status === "Reconciled" ? (
                          <span className="badge badge-success">Reconciled</span>
                        ) : (
                          <span className="badge badge-danger">Discrepancy ({inr(d.difference)})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
